// BLOCK_SCHEMAS: single source of truth for block types, default data,
// labels, descriptions, icons. Mirrors the original artifact, with the
// markdown-paste artifacts (e.g. `[Date.now](http://Date.now)()`) cleaned up.

import { Layout, MessageSquare, Cloud, Newspaper, Megaphone, List, BarChart3, Mail, Inbox, Calendar, type LucideIcon } from 'lucide-react';
import type { Block, BlockType } from '@shared/types';

// Re-export the pure block-data helpers so the existing editor
// codebase can keep importing from './blocks'. The actual logic
// lives in @shared/block-utils so the worker can use it during
// newsletter HTML rendering.
export {
  storyBodyItems,
  resolveCalendarMode,
  resolveCalendarEvents,
  dbEventToRenderEvent,
  type CalendarMode,
  type CalendarRenderEvent,
} from '@shared/block-utils';

export interface BlockSchema {
  label: string;
  icon: LucideIcon;
  description: string;
  // The shape used by `normalizeBlocks` to fill missing keys on stored
  // rows. MUST stay legacy-shaped — adding a new key here back-fills it
  // onto every existing draft on the next load (that's how the April
  // 2026 inline-images incident happened). Read CLAUDE.md before
  // touching.
  defaults: () => Record<string, unknown>;
  // Optional. The shape applied when a brand-new block is inserted via
  // `makeBlock`. Layered on top of `defaults()` so we can opt new
  // blocks into a different shape (e.g. calendar's auto-pull mode)
  // without risking the same field appearing in `defaults()` and
  // back-filling existing rows.
  freshInsert?: () => Record<string, unknown>;
}

export const BLOCK_SCHEMAS: Record<BlockType, BlockSchema> = {
  header: {
    label: 'Header',
    icon: Layout,
    description: 'Logo, date, edition number',
    defaults: () => ({
      logoUrl: 'https://cdn.penobscotmorning.com/static/logos/PenobscotMorningBlueFull.png',
      dateLine: formatToday(),
      editionNumber: '001',
      editionMode: 'numbered',
    }),
  },
  greeting: {
    label: 'Editor Note',
    icon: MessageSquare,
    description: 'Personal opener from the editor',
    defaults: () => ({
      lead: 'Good morning, Bangor.',
      body: 'Brief opener that sets the tone for the day. A small observation, a piece of weather context, a wink at something downtown. Two to four sentences.',
      signature: 'The Editor',
    }),
  },
  weather: {
    label: 'Weather',
    icon: Cloud,
    description: 'Today, tomorrow, week ahead, sunrise/sunset',
    defaults: () => ({
      todayTemp: '52°',
      todayLabel: 'Sunny',
      todayIcon: '☀',
      todayHigh: '58°',
      todayLow: '41°',
      tomorrowTemp: '48°',
      tomorrowLabel: 'Cloudy',
      tomorrowIcon: '⛅',
      tomorrowHigh: '52°',
      tomorrowLow: '39°',
      forecast: [
        { day: 'Mon', high: '54°', low: '38°', icon: '☀' },
        { day: 'Tue', high: '51°', low: '40°', icon: '⛅' },
        { day: 'Wed', high: '49°', low: '37°', icon: '🌧' },
        { day: 'Thu', high: '56°', low: '42°', icon: '☀' },
      ],
      sunrise: '5:36 AM',
      sunset: '7:42 PM',
      notes: '',
    }),
  },
  story: {
    label: 'Story',
    icon: Newspaper,
    description: 'Image + headline + structured body',
    // ⚠️ DO NOT add a top-level `body` field here. See CLAUDE.md
    // ("Story body migration"). The canonical body sequence is built
    // at read time by `storyBodyItems`, which lifts `paragraphs` into
    // the new shape on the fly. Adding a default `body` here would
    // cause normalizeBlocks to inject it over existing rows that only
    // have `paragraphs`, masking real user content.
    defaults: () => ({
      imageUrl: 'https://images.unsplash.com/photo-1567403041457-28611cb876f8?q=80&w=2000&auto=format&fit=crop',
      imageAlt: 'Story image',
      caption: 'Photo: Credit / Source',
      eyebrow: 'Around Town',
      headline: 'Headline goes here — make it specific and human',
      paragraphs: [
        { lead: '', body: 'Opening paragraph. Lead with what is new and why a Bangor resident would care.' },
        { lead: 'The context:', body: 'A short framing paragraph explaining the backstory.' },
        { lead: 'What it means:', body: 'Why it matters locally. Tie back to specific neighborhoods or businesses when possible.' },
        { lead: "What's next:", body: 'The forward-looking note. Something to watch.' },
      ],
      readMoreUrl: '#',
      readMoreLabel: 'Read more',
      readMoreStyle: 'link',
    }),
  },
  sponsor: {
    label: 'Sponsor',
    icon: Megaphone,
    description: 'Native-style sponsor section',
    defaults: () => ({
      sponsorName: 'Sponsor Name',
      headline: 'Sponsor headline that reads like editorial',
      body: '2-3 sentence sponsor blurb. Native-feeling copy converts way better than banner-style.',
      ctaLabel: 'Learn more',
      ctaUrl: '#',
    }),
  },
  bulletin: {
    label: 'Bulletin',
    icon: List,
    description: 'Quick hits from around the region',
    defaults: () => ({
      title: 'The Penobscot Bulletin',
      subtitle: 'Quick hits from around the region.',
      items: [
        { headline: 'Item one', body: 'One or two sentence summary.', sourceLabel: 'Source', sourceUrl: '#' },
        { headline: 'Item two', body: 'Summary text here.', sourceLabel: '', sourceUrl: '' },
        { headline: 'Item three', body: 'Summary text here.', sourceLabel: '', sourceUrl: '' },
      ],
    }),
  },
  calendar: {
    label: 'Calendar',
    icon: Calendar,
    description: 'Upcoming events, smart relative dates',
    // Legacy-only shape. We do NOT mention `source`, `hiddenEventIds`,
    // or `extraEvents` here — those keys would back-fill onto every
    // existing calendar block via normalizeBlocks. The sample event was
    // dropped: existing rows already have populated `events`, and a new
    // block goes through `freshInsert` below which seeds auto mode.
    defaults: () => ({
      title: 'On Your Calendar',
      subtitle: "What's happening this week",
      events: [],
    }),
    // Brand-new calendar blocks default to auto-pull. The first user
    // edit (or "Switch to manual" toggle) is the only writer of
    // `source`, so legacy rows that never go through freshInsert stay
    // untouched.
    freshInsert: () => ({
      title: 'On Your Calendar',
      subtitle: "What's happening this week",
      source: 'auto',
      events: [],
      hiddenEventIds: [],
      extraEvents: [],
    }),
  },
  poll: {
    label: 'Reader Poll',
    icon: BarChart3,
    description: 'Tappable poll + optional previous results',
    defaults: () => ({
      question: 'Poll question — local, opinionated, easy to answer in one tap',
      options: [
        { label: 'Option one', url: '#' },
        { label: 'Option two', url: '#' },
        { label: 'Option three', url: '#' },
      ],
      previousLabel: "Yesterday's poll",
      previousQuestion: '',
      previousResults: [] as Array<{ label: string; percent: number }>,
    }),
  },
  signoff: {
    label: 'Sign-off + Forward',
    icon: Mail,
    description: 'Closing line and forward CTA',
    defaults: () => ({
      closing: "That's it for today. See you Monday morning.",
      signature: '— The Penobscot Morning team',
      forwardPrompt: "Know someone who'd like a smarter Bangor morning?",
      forwardLabel: 'Forward this email →',
      forwardUrl: '#',
    }),
  },
  footer: {
    label: 'Footer',
    icon: Inbox,
    description: 'Editor contact, social links, address, unsubscribe',
    defaults: () => ({
      socials: [
        { label: 'Facebook', url: '#' },
        { label: 'X', url: '#' },
        { label: 'Instagram', url: '#' },
        { label: 'Tip Line', url: 'mailto:tips@penobscotmorning.com' },
      ],
      tagline: 'Penobscot Morning · A daily-ish dispatch from the Bangor region',
      address: '40 High Street, Suite 1, Bangor, ME 04401',
      editorEmail: 'editor@penobscotmorning.com',
      editorPhone: '(207) 401-7710',
    }),
  },
};

export const BLOCK_ORDER_DEFAULT: BlockType[] = [
  'header', 'greeting', 'weather', 'story', 'sponsor', 'bulletin', 'calendar', 'poll', 'signoff', 'footer',
];

export function formatToday(): string {
  const d = new Date();
  const day = d.toLocaleDateString('en-US', { weekday: 'long' });
  const month = d.toLocaleDateString('en-US', { month: 'long' });
  return `${day} · ${month} ${d.getDate()}, ${d.getFullYear()}`;
}

export function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export function makeBlock(type: BlockType): Block {
  const schema = BLOCK_SCHEMAS[type];
  const data = schema.freshInsert ? schema.freshInsert() : schema.defaults();
  return { id: uid(), type, data };
}

export function makeDefaultDraftBlocks(): Block[] {
  return BLOCK_ORDER_DEFAULT.map(makeBlock);
}

// Estimate of word count across all string values in a draft. Used in
// the right-side stats panel.
export function countWords(blocks: Block[]): number {
  let total = 0;
  const walk = (val: unknown): void => {
    if (typeof val === 'string') {
      total += val.trim().split(/\s+/).filter(Boolean).length;
    } else if (Array.isArray(val)) {
      val.forEach(walk);
    } else if (val && typeof val === 'object') {
      Object.values(val as Record<string, unknown>).forEach(walk);
    }
  };
  blocks.forEach((b) => walk(b.data));
  return total;
}
